// Shapes of the JSON API under /api. Mirrors DraftStateSerializer on the
// Rails side; extended in later phases (picks, clock).

export type DraftStatus = 'setup' | 'live' | 'paused' | 'complete'
export type DraftType = 'snake' | 'linear'
export type ExpiryPolicy = 'autopick' | 'skip' | 'wait'

export const ROSTER_SLOTS = ['QB', 'RB', 'WR', 'TE', 'FLEX', 'K', 'DST', 'BENCH'] as const
export type RosterSlot = (typeof ROSTER_SLOTS)[number]
export type RosterConfig = Record<RosterSlot, number>

export interface TeamState {
  id: number
  name: string
  owner_name: string | null
  draft_position: number | null
  claimed: boolean
  claimed_at: string | null
  autopick_enabled: boolean
}

export interface PickState {
  overall: number
  round: number
  slot: number
  team_id: number
  player_id: number | null
  status: 'pending' | 'made' | 'skipped'
  made_by: 'owner' | 'commissioner' | 'auto' | null
  keeper: boolean
  picked_at: string | null
}

export interface DraftState {
  join_code: string
  name: string
  status: DraftStatus
  draft_type: DraftType
  pick_seconds: number
  expiry_policy: ExpiryPolicy
  roster_config: RosterConfig
  rounds: number
  current_pick_overall: number | null
  state_version: number
  started_at: string | null
  completed_at: string | null
  deadline_at: string | null
  clock_generation: number
  paused_remaining_ms: number | null
  // When the draw is scheduled to run. Null when nothing is scheduled, and
  // null again after the commissioner cancels.
  lottery_at: string | null
  // The instant the server shuffled. Millisecond precision like deadline_at,
  // because every screen plays its animation from this one anchor. A one-way
  // latch: once set nothing clears it, and the draft order is locked.
  lottery_revealed_at: string | null
  teams: TeamState[]
  picks: PickState[]
  rankings_count: number
  my_team_id?: number
  // Private to the owner: only present when the request carried this team's
  // own token, never in broadcasts.
  my_queue?: number[]
}

// The players table stores Sleeper's "DEF"; the roster/UI name is "DST".
export interface PlayerSummary {
  id: number
  full_name: string
  position: string
  nfl_team: string | null
  search_rank: number
  // The draft's imported CSV rank when it listed this player, else
  // search_rank. The pool arrives already sorted by it.
  effective_rank: number
  custom_rank: boolean
  // Sleeper CDN headshot, derived at sync time. Null for manually added
  // players, and the CDN 403s for anyone it has no photo of — Portrait treats
  // both as "show the monogram".
  portrait_url: string | null
  // Sleeper's `number`, passed through. Null for a team defense (a DST has no
  // jersey), for manually added players, and for the players Sleeper simply
  // has no number for — every surface renders those as nothing at all.
  jersey_number: number | null
  // The week 1-18 this player's NFL team doesn't play, derived from Sleeper's
  // schedule at sync time. Null for free agents and until the first successful
  // sync — surfaces omit the segment rather than render a placeholder.
  bye_week: number | null
  // -- Dossier: Sleeper profile, passed through untouched. Null everywhere
  // Sleeper had no data, and permanently null on locked and manual-* rows —
  // the sync deliberately never writes these to either. Every player-facing
  // surface omits a null field rather than printing a placeholder.
  injury_status: string | null
  injury_body_part: string | null
  injury_notes: string | null
  /** ISO calendar date, "YYYY-MM-DD". */
  injury_start_date: string | null
  /** Free-text Sleeper string, not an enum — display it, never switch on it. */
  practice_participation: string | null
  /** Sleeper's own designation ("Active", "Injured Reserve"), distinct from
   *  the pool's `active` flag. */
  roster_status: string | null
  height_inches: number | null
  weight_lbs: number | null
  age: number | null
  /** 0 is a rookie, which is not the same as null. */
  years_exp: number | null
  college: string | null
  /** Sleeper's depth-chart slot, not always equal to `position`. */
  depth_chart_position: string | null
  depth_chart_order: number | null
  /** ISO 8601 UTC. When Sleeper last touched this player's news feed, as of
   *  the last sync — NOT as of now. The sync is rate-limited to one run per
   *  12h, so this must never be rendered beside a live news item. */
  news_updated_at: string | null
}

export interface DraftResponse {
  draft: DraftState
  // The server's clock when it answered. Envelope, not draft state — the same
  // field every cable event carries, and it feeds recordServerTime so a page
  // that renders a countdown before its first broadcast is still synchronized.
  server_now_ms: number
}

export interface CreateDraftResponse extends DraftResponse {
  commissioner_token: string
}

export interface CreateTeamResponse extends DraftResponse {
  team_id: number
}

export interface ClaimResponse extends DraftResponse {
  team_token: string
}

export interface PlayersResponse {
  players: PlayerSummary[]
}

// -- Pick queues and rankings (Phase 8) -------------------------------------

// Ordered player ids. Entries stay after the player is drafted — the UI
// strikes them through and autopick steps over them.
export interface QueueResponse {
  queue: number[]
}

export interface RankingsMatchedRow {
  line: number
  rank: number
  name: string
  position: string | null
  team: string | null
  player_id: number
  player_name: string
  player_position: string
  player_team: string | null
}

export interface RankingsUnmatchedRow {
  line: number
  rank: number
  name: string | null
  position: string | null
  team: string | null
  reason: string
}

export interface RankingsImportResponse {
  count: number
  matched: RankingsMatchedRow[]
  unmatched: RankingsUnmatchedRow[]
}

// -- Admin dashboard (Phase 8.5) --------------------------------------------

export interface SyncRun {
  id: number
  source: 'rake' | 'api'
  status: 'running' | 'success' | 'failed'
  started_at: string | null
  finished_at: string | null
  players_upserted: number | null
  players_total_after: number | null
  // How many of the 32 teams resolved to a bye on that run. 0 means the
  // schedule step degraded and error_message says why — on a `success` row,
  // error_message is a warning rather than a failure.
  bye_weeks_derived: number | null
  error_message: string | null
}

export interface SyncResponse {
  sync_run: SyncRun
}

export interface AdminOverview {
  players: {
    total: number
    active: number
    inactive: number
    locked: number
    manual: number
    by_position: Record<string, number>
    missing_nfl_team: number
    unranked: number
  }
  last_sync: SyncRun | null
  // Null when an unforced sync is allowed right now.
  next_sync_available_at: string | null
  recent_syncs: SyncRun[]
  drafts: {
    total: number
    by_status: Record<string, number>
  }
}

export interface AdminPlayer {
  id: number
  sleeper_id: string
  full_name: string
  name_key: string
  position: string
  nfl_team: string | null
  search_rank: number
  active: boolean
  locked: boolean
  manual: boolean
  // Sleeper-derived profile. Null everywhere Sleeper has no data — read-only
  // in the admin table, overwritten in place by every sync.
  injury_status: string | null
  roster_status: string | null
  years_exp: number | null
  jersey_number: number | null
  depth_chart_position: string | null
  depth_chart_order: number | null
  height_inches: number | null
  weight_lbs: number | null
  age: number | null
  // ISO "YYYY-MM-DD".
  birth_date: string | null
  college: string | null
  injury_body_part: string | null
  injury_notes: string | null
  // ISO "YYYY-MM-DD".
  injury_start_date: string | null
  // Free-text Sleeper string, not an enum.
  practice_participation: string | null
  // ISO 8601 UTC — when Sleeper last touched this player's news feed, as of
  // the last sync.
  news_updated_at: string | null
  // Derived from Sleeper's schedule rather than the player dump.
  bye_week: number | null
  // Sleeper CDN headshot (team logo for DEF), null for manual-* players.
  portrait_url: string | null
  updated_at: string
}

export interface AdminPlayersResponse {
  players: AdminPlayer[]
  total_count: number
  page: number
  page_count: number
}

export interface AdminPlayerResponse {
  player: AdminPlayer
}

export interface AdminDraft {
  id: number
  name: string
  join_code: string
  status: DraftStatus
  team_count: number
  picks_made: number
  picks_total: number
  created_at: string
}

export interface AdminDraftsResponse {
  drafts: AdminDraft[]
}

export interface CommissionerLinkResponse {
  url: string
}

// -- Cable protocol types (Phase 6) -----------------------------------------

export type ClockStateValue = 'running' | 'paused' | 'waiting' | 'none'

export interface ClockState {
  state: ClockStateValue
  deadline_ms: number | null
  seconds_total: number
  remaining_ms_hint: number | null
}

export interface OnClockState {
  overall: number
  round: number
  slot: number
  team_id: number
  on_deck_team_id: number | null
}

export interface DenormalizedPlayer {
  id: number
  name: string
  position: string
  nfl_team: string | null
}

// Event envelope base — every broadcast carries these fields.
export interface EventEnvelope {
  type: string
  state_version: number
  server_now_ms: number
}

// Discriminated union of all cable event types.
export interface DraftStartedEvent extends EventEnvelope {
  type: 'draft_started'
  state: DraftState
}

export interface PickMadeEvent extends EventEnvelope {
  type: 'pick_made'
  pick: {
    overall: number
    round: number
    slot: number
    team_id: number
    made_by: string
    player: DenormalizedPlayer
  }
  on_clock: OnClockState | null
  clock: ClockState
  completed: boolean
}

export interface PickUndoneEvent extends EventEnvelope {
  type: 'pick_undone'
  overall: number
  on_clock: OnClockState | null
  clock: ClockState
}

export interface PickSkippedEvent extends EventEnvelope {
  type: 'pick_skipped'
  overall: number
  on_clock: OnClockState | null
  clock: ClockState
}

export interface ClockStartedEvent extends EventEnvelope {
  type: 'clock_started'
  on_clock: OnClockState | null
  clock: ClockState
}

export interface ClockExpiredEvent extends EventEnvelope {
  type: 'clock_expired'
  on_clock: OnClockState | null
  clock: ClockState
}

export interface DraftPausedEvent extends EventEnvelope {
  type: 'draft_paused'
  on_clock: OnClockState | null
  clock: ClockState
}

export interface DraftResumedEvent extends EventEnvelope {
  type: 'draft_resumed'
  on_clock: OnClockState | null
  clock: ClockState
}

export interface DraftCompletedEvent extends EventEnvelope {
  type: 'draft_completed'
}

export interface TeamsUpdatedEvent extends EventEnvelope {
  type: 'teams_updated'
  teams: TeamState[]
}

export interface SettingsUpdatedEvent extends EventEnvelope {
  type: 'settings_updated'
  settings: {
    name: string
    draft_type: DraftType
    pick_seconds: number
    expiry_policy: ExpiryPolicy
    roster_config: RosterConfig
  }
}

export interface OrderRandomizedEvent extends EventEnvelope {
  type: 'order_randomized'
  order: Array<{ id: number; draft_position: number }>
}

// Schedule and cancel share one event type: `lottery_at` is null on a cancel.
// The client's job is identical either way — replace the field — and a second
// type would be a second reducer case doing the same assignment.
export interface LotteryScheduledEvent extends EventEnvelope {
  type: 'lottery_scheduled'
  lottery_at: string | null
}

// Full serialized state, like DraftStartedEvent. During setup `picks` is
// empty, so the payload is a dozen teams and some scalars — cheap, and it
// removes three chances for the reducer to drift from what a refetch would
// produce.
export interface LotteryRevealedEvent extends EventEnvelope {
  type: 'lottery_revealed'
  state: DraftState
}

export interface RankingsImportedEvent extends EventEnvelope {
  type: 'rankings_imported'
  count: number
}

export type ServerEvent =
  | DraftStartedEvent
  | PickMadeEvent
  | PickUndoneEvent
  | PickSkippedEvent
  | ClockStartedEvent
  | ClockExpiredEvent
  | DraftPausedEvent
  | DraftResumedEvent
  | DraftCompletedEvent
  | TeamsUpdatedEvent
  | SettingsUpdatedEvent
  | OrderRandomizedEvent
  | LotteryScheduledEvent
  | LotteryRevealedEvent
  | RankingsImportedEvent

// -- Results (Phase 9) ------------------------------------------------------

// GET /api/drafts/:code/results. A one-shot snapshot, not a live feed: the
// results page reads it once and never subscribes to the cable.

// Nearly DenormalizedPlayer, plus the portrait: the results page has no player
// pool to look one up in, where a cable consumer does (the reducer only reads
// `id` off the event and takes the rest from the pool). Same caveat on
// `position` — it is the raw "DEF", which the UI renders as "DST".
export interface ResultsPlayer {
  id: number
  name: string
  position: string
  nfl_team: string | null
  portrait_url: string | null
}

export interface ResultsPick {
  overall: number
  round: number
  slot: number
  team_id: number
  team_name: string
  owner_name: string | null
  status: 'pending' | 'made' | 'skipped'
  made_by: 'owner' | 'commissioner' | 'auto' | null
  picked_at: string | null
  // Null for pending and skipped picks.
  player: ResultsPlayer | null
}

// Slot comes from the server's RosterBuilder greedy fill, so it agrees with
// the fit check autopick used during the draft.
export interface ResultsRosterEntry {
  slot: RosterSlot
  overall: number
  round: number
  player: ResultsPlayer
}

export interface ResultsTeam {
  id: number
  name: string
  owner_name: string | null
  draft_position: number | null
  roster: ResultsRosterEntry[]
}

export interface ResultsDraft {
  join_code: string
  name: string
  status: DraftStatus
  draft_type: DraftType
  rounds: number
  roster_config: RosterConfig
  started_at: string | null
  completed_at: string | null
}

export interface ResultsResponse {
  draft: ResultsDraft
  picks: ResultsPick[]
  teams: ResultsTeam[]
}

// -- Player news (Phase 24) ------------------------------------------------

/** "ok" — we reached Sleeper, and an empty list means it genuinely has nothing
 *  for this player. "unavailable" — we could not reach it, and the news section
 *  renders as absent rather than as an error. Sleeper itself cannot tell the
 *  two apart (it answers 200 [] for everything), so the server owns this field. */
export type PlayerNewsStatus = 'ok' | 'unavailable'

export interface NewsItem {
  /** Unique within one response only. A React key, not an identity — do not persist. */
  id: string
  title: string
  description: string | null
  analysis: string | null
  /** An absolute https URL or null; the server nulls anything else. */
  url: string | null
  source: string | null
  /** ISO 8601 UTC, converted server-side from Sleeper's epoch milliseconds. */
  published_at: string | null
}

export interface PlayerNewsResponse {
  player_id: number
  status: PlayerNewsStatus
  news: NewsItem[]
}
